// Tiny router on the History API. Routes are matched in order; ":name" captures one segment.
import { h, createContext } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';

const RouteContext = createContext({ path: '/', query: new URLSearchParams() });
const listeners = new Set();

function current() {
  return { path: location.pathname.replace(/\/+$/, '') || '/', query: new URLSearchParams(location.search) };
}

// A screen with unsaved changes asks before the app moves away (links, the back button, closing).
let leaveGuard = null;
let shown = location.pathname + location.search;

export function useLeaveGuard(active, message = 'Leave without saving your changes?') {
  useEffect(() => {
    if (!active) return undefined;
    const guard = { message };
    leaveGuard = guard;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => { if (leaveGuard === guard) leaveGuard = null; window.removeEventListener('beforeunload', warn); };
  }, [active, message]);
}

// force: after the screen has saved (its guard may not have been taken down yet).
export function navigate(to, { replace = false, force = false } = {}) {
  if (to === location.pathname + location.search) return;
  if (leaveGuard && !force && !window.confirm(leaveGuard.message)) return;
  leaveGuard = null;
  history[replace ? 'replaceState' : 'pushState']({}, '', to);
  shown = to;
  window.scrollTo(0, 0);
  listeners.forEach((fn) => fn());
}

export function RouterProvider({ children }) {
  const [route, setRoute] = useState(current);
  useEffect(() => {
    const update = () => setRoute(current());
    const back = () => {
      const target = location.pathname + location.search;
      if (leaveGuard && !window.confirm(leaveGuard.message)) { history.pushState({}, '', shown); return; }
      leaveGuard = null;
      shown = target;
      update();
    };
    listeners.add(update);
    window.addEventListener('popstate', back);
    return () => { listeners.delete(update); window.removeEventListener('popstate', back); };
  }, []);
  return h(RouteContext.Provider, { value: route }, children);
}

export function useRoute() {
  return useContext(RouteContext);
}

export function match(pattern, path) {
  const a = pattern.split('/').filter(Boolean);
  const b = path.split('/').filter(Boolean);
  if (a.length !== b.length) return null;
  const params = {};
  for (let i = 0; i < a.length; i++) {
    if (a[i].startsWith(':')) params[a[i].slice(1)] = decodeURIComponent(b[i]);
    else if (a[i] !== b[i]) return null;
  }
  return params;
}

// <Link href="/batches/1">…</Link>: a normal link that changes page without reloading.
export function Link({ href, children, onClick, ...rest }) {
  return h('a', {
    href,
    ...rest,
    onClick: (e) => {
      if (onClick) onClick(e);
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (rest.target === '_blank') return;
      e.preventDefault();
      navigate(href);
    },
  }, children);
}
